import React, { useState, useEffect, useRef, useContext } from "react";
import { Link } from "react-router-dom";

// Bootstrap components
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";

// Context
import AlertContext from "../../context/AlertContext";
import { useAuth } from "../../context/AuthContext";

// Custom components
import Timer from "react-compound-timer";
import moment from "moment-timezone";
import { Carousel } from "react-responsive-carousel";

// Custom CSS
import "./raffleProduct.styles.scss";

// API
import { postBid } from "../../api/raffle";
import { getAuctionProduct } from "../../api/product";

// Utils
import formatMoney from "../../utils/formatMoney";
import formatShortName from "../../utils/formatShortName";
import useForm from "../../utils/useForm";
import {
  BID_COMMITMENT_COPY,
  getMinimumNextBid,
  isAuctionClosed,
  isAuctionOpenForBidding,
  mapBidErrorMessage,
} from "../../utils/auctionStatus";

export default function RaffleProduct({
  product,
  isSubmittingBid,
  setIsSubmittingBid,
}) {
  // Initialize bids with fallback to empty array
  const [bids, setBids] = useState(product?.bids || []);
  const [auctionProduct, setAuctionProduct] = useState(product);
  const { auth, logout, user } = useAuth();
  const timer = useRef();

  const alert = useContext(AlertContext);

  const counterValue = () => {
    let endTimeMexicoCity;

    if (product?.horaFinal && product?.fechaFinal) {
      // Get the date from fechaFinal and time from horaFinal, both in Mexico City timezone
      const fechaFinalMexico = moment.tz(product.fechaFinal, "America/Mexico_City");
      const horaFinalMexico = moment.tz(product.horaFinal, "America/Mexico_City");

      // Combine the date from fechaFinal with the time from horaFinal
      endTimeMexicoCity = fechaFinalMexico.clone().hour(horaFinalMexico.hour()).minute(horaFinalMexico.minute()).second(horaFinalMexico.second());
    } else {
      // Fallback to original logic
      const dateFromApi = product?.horaFinal || product?.fechaFinal;
      endTimeMexicoCity = moment.utc(dateFromApi).tz("America/Mexico_City");
    }

    const now = moment.tz("America/Mexico_City");
    const duration = endTimeMexicoCity.diff(now, "milliseconds");

    // Debug logging
    console.log("=== TIMEZONE DEBUG ===");
    console.log("API horaFinal (UTC):", product?.horaFinal);
    console.log("API fechaFinal (UTC):", product?.fechaFinal);
    console.log("Combined end time in Mexico City:", endTimeMexicoCity.format("YYYY-MM-DD HH:mm:ss Z"));
    console.log("Current time in Mexico City:", now.format("YYYY-MM-DD HH:mm:ss Z"));
    console.log("Duration in milliseconds:", duration);
    console.log("Duration in hours:", duration / (1000 * 60 * 60));
    console.log("=====================");

    return duration;
  };

  // Initialize count with proper fallback
  const [count, setCount] = useState(() => {
    console.log("Initial state calculation - product:", product);
    console.log("Initial state calculation - bids:", bids);

    if (!product) return 0;

    const safeBids = Array.isArray(bids) ? bids : [];
    if (safeBids.length === 0) {
      console.log("No bids, using pujaInicial:", product.pujaInicial);
      return product.pujaInicial || 0;
    }

    // Force getting the actual last element
    const lastBid = safeBids[safeBids.length - 1];
    const lastBidAmount = lastBid?.bid || 0;
    const increment = product.pujaSubsecuente || 0;
    const nextBid = lastBidAmount + increment;

    console.log(
      "Initial calculation - Last bid:",
      lastBidAmount,
      "Next bid:",
      nextBid
    );
    return nextBid;
  });

  const handleIncrement = () => {
    setCount((prevCount) => prevCount + (product?.pujaSubsecuente || 0));
  };

  const handleDecrement = () => {
    const safeBidsLocal = Array.isArray(bids) ? bids : [];
    const minimumNextBid = getMinimumNextBid(auctionProduct || product, safeBidsLocal);

    if (count > minimumNextBid) {
      setCount((prevCount) => prevCount - (product?.pujaSubsecuente || 0));
    }
  };

  const { inputs, handleChange, resetForm } = useForm({
    user: user?.username || user?.user?.username || "",
    bid: 0,
  });

  async function refreshAuctionData() {
    const refreshed = await getAuctionProduct(product.id);
    if (refreshed && !refreshed.error) {
      setAuctionProduct(refreshed);
      setBids(refreshed.bids || []);
      setCount(getMinimumNextBid(refreshed, refreshed.bids || []));
    }
    return refreshed;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const userId = user?.user?.id || user?.id || auth?.idUser;
    if (!userId || !product.id) {
      alert.error("Error: Usuario o producto no válido.");
      return;
    }

    setIsSubmittingBid(true);

    try {
      const response = await postBid(product.id, count, logout);

      if (response?.error) {
        if (response.errorCode === 409) {
          await refreshAuctionData();
        }
        alert.error(mapBidErrorMessage(response));
        return;
      }

      if (!response?.doc) {
        alert.error("Hubo un error en tu solicitud de puja.");
        return;
      }

      alert.success("Tu puja fue publicada exitosamente.");
      const newBids = response.doc.bids || [];
      setAuctionProduct(response.doc);
      setBids(newBids);
      setCount(getMinimumNextBid(response.doc, newBids));
      resetForm({
        user: user?.username || user?.user?.username || "",
      });
    } catch (error) {
      console.error("Error submitting bid:", error);
      alert.error(mapBidErrorMessage(error));
    } finally {
      setIsSubmittingBid(false);
    }
  }

  useEffect(() => {
    setAuctionProduct(product);
    setBids(product?.bids || []);
  }, [product]);

  useEffect(() => {
    const safeBids = Array.isArray(bids) ? [...bids] : [];
    setCount(getMinimumNextBid(auctionProduct || product, safeBids));
  }, [bids, auctionProduct, product]);

  const safeBids = Array.isArray(bids) ? [...bids] : [];
  const duration = counterValue();
  const auctionActive = isAuctionOpenForBidding(auctionProduct || product, duration);
  const auctionEnded = isAuctionClosed(auctionProduct || product, duration);

  return (
    <Col xs="6" lg="3" className="raffle-product-card">
      {/* Removed local loading overlay - now handled by parent */}

      <div className="raffle-product-card__media">
        <Carousel
          showStatus={false}
          showIndicators={false}
          showThumbs={true}
          renderThumbs={(children) => {
            return product?.imagenes?.map((image, key) => (
              <Link
                to={`/auction-product/${product.id}`}
                key={key}
                style={{
                  display: "block",
                  height: "100%",
                  width: "100%",
                  cursor: "pointer"
                }}
                onClick={(e) => {
                  // Prevent the default carousel slide change behavior
                  e.stopPropagation();
                }}
              >
                <img
                  src={`${image.image.url}`}
                  alt={`${product?.nombreProducto} - Vista ${key + 1}`}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover"
                  }}
                />
              </Link>
            ));
          }}
        >
          {product?.imagenes?.map((image, key) => (
            <Link
              to={`/auction-product/${product.id}`}
              key={key}
              style={{ display: "block", height: "100%", width: "100%" }}
            >
              <img src={`${image.image.url}`} alt={`${product?.nombreProducto} - Vista ${key + 1}`} />
            </Link>
          ))}
        </Carousel>
      </div>
      <div className="raffle-product-card__body">
        <Link to={`/auction-product/${product.id}`}>
          <h5>{product?.nombreProducto}</h5>
        </Link>
        {auctionActive ? (
          <>
            <span>La subasta termina en:</span>
            <p className="raffle-product-card__timer">
              <Timer
                initialTime={duration}
                direction="backward"
                ref={timer}
                checkpoints={[
                  {
                    time: 0,
                  },
                ]}
              >
                {() => (
                  <>
                    <Timer.Days /> días <Timer.Hours /> horas <Timer.Minutes />{" "}
                    minutos <Timer.Seconds /> segundos{" "}
                  </>
                )}
              </Timer>
            </p>
          </>
        ) : (
          <p
            style={{
              color: "#fff",
              fontWeight: "bolder",
              backgroundColor: "#E10009",
              padding: ".5rem 0",
              borderRadius: "20px",
              marginTop: "10px",
            }}
            className="mb-2 text-center"
          >
            La subasta ha finalizado
          </p>
        )}
        <h6 className="mt-3">
          {formatMoney(product?.precio)} MXN <span></span>
        </h6>
        <span>Precio del reloj en el mercado</span>
        {auctionEnded && safeBids.length > 0 && (
          <div className="product__winner">
            <p style={{ marginTop: "0" }}>
              🎉 Ganador: 🎉{" "}
              <span style={{ display: "inline-block" }}>
                {formatShortName(safeBids[safeBids.length - 1]?.usuario)}
              </span>
              <a
                href={`https://wa.me/525568757619?text=${encodeURIComponent(
                  `¡Hola! Soy ${safeBids[safeBids.length - 1]?.usuario
                  }, el ganador de la subasta del ${product?.nombreProducto
                  }. Me gustaría reclamar mi premio. Mi puja ganadora fue de ${formatMoney(
                    safeBids[safeBids.length - 1]?.bid
                  )} MXN.`
                )}`}
                target="_blank"
                rel="noreferrer"
              >
                <i className=" fab fa-whatsapp"></i> Comunícate con nosotros
                para reclamar tu premio
              </a>
            </p>
          </div>
        )}

        {auctionActive && (
          <span className="raffle-product-card__bids__next-bid">
            La siguiente puja es de:{" "}
            {formatMoney(getMinimumNextBid(auctionProduct || product, safeBids))}
          </span>
        )}

        <h4 className="mt-2">Pujas:</h4>
        <div className="raffle-product-card__bids__container">
          {safeBids.length === 0 ? (
            <p className="raffle-product-card__bids__empty">
              No hay pujas en este momento
            </p>
          ) : (
            [...safeBids]
              .sort((a, b) => (b?.bid || 0) - (a?.bid || 0))
              .map((bid, key) => (
                <div key={key} className="raffle-product-card__bids">
                  <div className="d-flex justify-content-between w-100">
                    <p>
                      <span>Nombre: </span>
                      {formatShortName(bid?.usuario) || "Usuario desconocido"}
                    </p>
                    <p className="mt-2 mt-lg-0">
                      <span>Puja: </span>
                      {formatMoney(bid?.bid || 0)} MXN
                    </p>
                  </div>
                </div>
              ))
          )}
        </div>

        <div className="raffle-product-card__bids__input">
          {!auth?.idUser && (
            <p>
              Debes <Link to="/login">iniciar sesión</Link> o{" "}
              <Link to="/register">registrarte</Link> para publicar una puja.
            </p>
          )}
          {auth.idUser && auctionActive ? (
            <Form onSubmit={handleSubmit} id="bid-form">
              <Row>
                <Form.Group className="mb-1" controlId="formName">
                  <Form.Label>Nombre de usuario:</Form.Label>
                  <Form.Control
                    name="user"
                    plaintext
                    defaultValue={user?.user?.fullName || ""}
                    readOnly
                  />
                  <Form.Text className="text-muted">
                    Este es tu nombre de usuario
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-2" controlId="formBid">
                  <Form.Label>Oferta:</Form.Label>
                  <div className="bid-container">
                    <button
                      type="button"
                      className="counter-action"
                      disabled={
                        isSubmittingBid ||
                        count <= getMinimumNextBid(auctionProduct || product, safeBids)
                      }
                      onClick={handleDecrement}
                    >
                      -
                    </button>
                    <h5 className="form-control">{formatMoney(count)}</h5>
                    <button
                      type="button"
                      className="counter-action"
                      disabled={isSubmittingBid}
                      onClick={handleIncrement}
                    >
                      +
                    </button>
                  </div>
                </Form.Group>
                <p className="raffle-product-card__bid-commitment text-muted">
                  {BID_COMMITMENT_COPY}
                </p>
              </Row>
              <Button
                className="send"
                type="submit"
                disabled={isSubmittingBid}
                style={{
                  opacity: isSubmittingBid ? 0.7 : 1,
                  cursor: isSubmittingBid ? "not-allowed" : "pointer",
                }}
              >
                {isSubmittingBid ? "Enviando..." : "Enviar puja"}
              </Button>
            </Form>
          ) : (
            <></>
          )}
          {auctionEnded && <p>La subasta ha finalizado</p>}
        </div>
      </div>
    </Col>
  );
}
